import {deleteField, Timestamp} from 'firebase/firestore';
import {useCallback} from 'react';
import {TodoRepeatUnit, type TodoDb} from '@cp/model';
import updateTodoDoc from '../../api/todo/updateTodoDoc';
import {useFunctionWrap} from '../helper/function/useFunctionWrap';

export interface UpdateTodoInput {
  title?: string;
  note?: string;
  done?: Timestamp | null;
  repeat?: TodoDb['repeat'];
  status?: TodoDb['status'];
  tags?: string[];
}

export default function useUpdateTodo(uid: string | undefined) {
  const updateFn = useCallback(
    (todoId: string, input: UpdateTodoInput) => {
      if (!uid) throw new Error('Todo collection not found');
      const updateData: Parameters<typeof updateTodoDoc>[2] = {};
      if (input.title !== undefined) updateData.title = input.title;
      if (input.note !== undefined) updateData.note = input.note;
      if (input.done !== undefined) updateData.done = input.done;
      if (input.status !== undefined) updateData.status = input.status;
      if (input.tags !== undefined) updateData.tags = input.tags;
      if (input.repeat !== undefined) {
        if (input.repeat === null) {
          updateData.repeat = deleteField();
        } else {
          const cleanRepeat: TodoDb['repeat'] = {
            unit: input.repeat.unit,
            startDate: input.repeat.startDate,
          };
          if (input.repeat.unit !== TodoRepeatUnit.DAY_OF_WEEK && input.repeat.number !== undefined) {
            cleanRepeat.number = input.repeat.number;
          }
          if (input.repeat.unit === TodoRepeatUnit.DAY_OF_WEEK && input.repeat.daysOfWeek !== undefined) {
            cleanRepeat.daysOfWeek = input.repeat.daysOfWeek;
          }
          updateData.repeat = cleanRepeat;
        }
      }
      return updateTodoDoc(uid, todoId, updateData);
    },
    [uid],
  );
  const {fn, loading, error} = useFunctionWrap(updateFn);

  return {
    updateTodo: fn,
    loading,
    error,
  };
}
